import { ChangeEvent, Dispatch, SetStateAction, useCallback } from 'react';

/*
 * Sometimes useState can have complex objects
 * For example: const [a, setA] = useState<{inner: {value: number}}>();
 * this function allows to bring out the inner objects into a new getter/setter
 * const [inner, setInner] = useObject(a, setA, 'inner');
 */
export function useObject<T extends object, U extends keyof T>(
  object: T,
  setter: Dispatch<(prevState: T) => T>,
  key: U,
): [T[U], Dispatch<SetStateAction<T[U]>>] {
  const setPayload = useCallback(
    (newPayload: SetStateAction<T[U]>) => {
      if (typeof newPayload === 'function') {
        const func = newPayload as (prevState: T[U]) => T[U];
        setter((old) => ({
          ...old,
          [key]: func(old[key]),
        }));
      } else {
        setter((old) => ({
          ...old,
          [key]: newPayload,
        }));
      }
    },
    [setter, key],
  );

  return [object[key], setPayload];
}

export type EventHandlerFn = (event: ChangeEvent<HTMLInputElement>) => void;
export function useInputModel<T extends object>(
  setter: Dispatch<(prevState: T) => T>,
  key: Extract<keyof T, string>,
): EventHandlerFn {
  return useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = (e.target.value as typeof key) || null;
      setter((old) => ({
        ...old,
        [key]: value,
      }));
    },
    [key, setter],
  );
}

export function useInputCheckboxModel<T extends object>(
  setter: Dispatch<(prevState: T) => T>,
  key: Extract<keyof T, string>,
): EventHandlerFn {
  return useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setter((old) => ({
        ...old,
        [key]: e.target.checked as unknown as typeof key,
      }));
    },
    [key, setter],
  );
}
